import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User, Task, Notification } from '../types';
import { getAllPatients } from '../firebase';
import { canViewPatient } from '../utils/roleUtils';

export function useTasksAndNotifications(user: User | null, language: string) {
  const [todayTasks, setTodayTasks] = useState<Task[]>([]);
  const [pendingTasks, setPendingTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);

  // Load tasks and notifications
  const loadTasksAndNotifications = useCallback(async () => {
    if (!user) return;
    
    setIsTasksLoading(true);
    setIsNotificationsLoading(true);
    
    try {
      // For social_worker and instructor, get their assigned patients first
      let userAssignedCaseIds: string[] = [];
      const isSocialWorkerOrInstructor = user.organizationRole === 'social_worker' || user.organizationRole === 'instructor';
      
      if (isSocialWorkerOrInstructor) {
        try {
          const allPatients = await getAllPatients();
          // Filter patients assigned to this user
          const assignedPatients = allPatients.filter(patient => 
            Array.isArray(patient.assignedAdmins) && patient.assignedAdmins.includes(user.id)
          );
          userAssignedCaseIds = assignedPatients.map(p => p.caseId).filter(Boolean);
          console.log('User assigned case IDs:', userAssignedCaseIds);
        } catch (error) {
          console.error('Error loading assigned patients:', error);
        }
      }
      
      // Load tasks
      const tasksRef = collection(db, 'tasks');
      let tasksQuery;
      
      if (isSocialWorkerOrInstructor && userAssignedCaseIds.length > 0) {
        // For social workers and instructors, get tasks for their assigned cases
        // Note: Firestore 'in' query limit is 10, so we may need to batch if more than 10 cases
        const caseIdsBatch = userAssignedCaseIds.slice(0, 10);
        tasksQuery = query(
          tasksRef,
          where('caseId', 'in', caseIdsBatch),
          orderBy('createdAt', 'desc')
        );
      } else {
        // For other roles, get tasks created by them
        tasksQuery = query(
          tasksRef,
          where('createdBy', '==', user.email),
          orderBy('createdAt', 'desc')
        );
      }
      
      const tasksSnapshot = await getDocs(tasksQuery);
      console.log('Loaded tasks from Firebase:', tasksSnapshot.docs.length, 'tasks');
      console.log('User email for filtering:', user.email);
      
      let allTasksData: Task[] = tasksSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          description: data.description || '',
          caseId: data.caseId || '',
          priority: data.priority || 'medium',
          status: data.status || 'pending',
          dueDate: data.dueDate || '',
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          createdBy: data.createdBy || user.id,
          updatedAt: data.updatedAt?.toDate?.()?.toISOString(),
          updatedBy: data.updatedBy || '',
          patientName: data.patientName || ''
        };
      });
      
      // Additional filtering for social workers and instructors
      if (isSocialWorkerOrInstructor && userAssignedCaseIds.length > 0) {
        allTasksData = allTasksData.filter(task => {
          // Include tasks with caseId that matches assigned cases
          if (task.caseId && userAssignedCaseIds.includes(task.caseId)) {
            return true;
          }
          // Also include tasks without caseId created by the user
          if (!task.caseId && task.createdBy === user.email) {
            return true;
          }
          return false;
        });
      }
      
      // If no tasks exist, create some sample data for demonstration
      if (allTasksData.length === 0) {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        let sampleTasks: Task[] = [
          {
            id: 'sample-task-1',
            title: language === 'he' ? 'פגישה עם מטופל חדש' : 'Meeting with new patient',
            description: language === 'he' ? 'פגישת היכרות עם מטופל חדש' : 'Initial consultation with new patient',
            caseId: 'CASE-001',
            priority: 'high',
            status: 'pending',
            dueDate: today.toISOString().split('T')[0],
            createdAt: today.toISOString(),
            createdBy: user.id,
            patientName: language === 'he' ? 'יוסי כהן' : 'Yossi Cohen'
          },
          {
            id: 'sample-task-2',
            title: language === 'he' ? 'השלמת דוח חודשי' : 'Complete monthly report',
            description: language === 'he' ? 'השלמת דוח פעילות חודשי' : 'Complete monthly activity report',
            caseId: '',
            priority: 'medium',
            status: 'pending',
            dueDate: today.toISOString().split('T')[0],
            createdAt: today.toISOString(),
            createdBy: user.id
          },
          {
            id: 'sample-task-3',
            title: language === 'he' ? 'מעקב אחר מטופל' : 'Patient follow-up',
            description: language === 'he' ? 'מעקב אחר התקדמות מטופל' : 'Follow up on patient progress',
            caseId: 'CASE-002',
            priority: 'low',
            status: 'pending',
            dueDate: yesterday.toISOString().split('T')[0],
            createdAt: yesterday.toISOString(),
            createdBy: user.id,
            patientName: language === 'he' ? 'שרה לוי' : 'Sara Levi'
          }
        ];
        
        // Filter sample tasks for social workers and instructors
        if (isSocialWorkerOrInstructor && userAssignedCaseIds.length > 0) {
          sampleTasks = sampleTasks.filter(task => {
            // Include tasks with caseId that matches assigned cases
            if (task.caseId && userAssignedCaseIds.includes(task.caseId)) {
              return true;
            }
            // Also include tasks without caseId created by the user
            if (!task.caseId && task.createdBy === user.id) {
              return true;
            }
            return false;
          });
        }
        
        setTodayTasks(sampleTasks.filter(task => {
          if (!task.dueDate) return false;
          const taskDate = new Date(task.dueDate).toISOString().split('T')[0];
          const todayString = today.toISOString().split('T')[0];
          return taskDate === todayString && task.status !== 'completed';
        }));
        
        setPendingTasks(sampleTasks.filter(task => {
          if (!task.dueDate) return task.status === 'pending';
          const taskDate = new Date(task.dueDate);
          const isOverdue = taskDate < today && task.status !== 'completed';
          if (isOverdue) {
            task.status = 'overdue';
          }
          return task.status === 'pending' || isOverdue;
        }));
        setAllTasks(sampleTasks); // Store all sample tasks
      } else {
        // Filter tasks for today and pending
        const today = new Date();
        // Use local date string to avoid timezone issues
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const todayString = `${year}-${month}-${day}`;
        
        // Filter out completed tasks first
        const activeTasks = allTasksData.filter(task => task.status !== 'completed');
        
        const todayTasksFiltered = activeTasks.filter(task => {
          if (!task.dueDate) {
            console.log('Task has no dueDate:', task.title);
            return false;
          }
          // If dueDate is already a string in YYYY-MM-DD format, use it directly
          // Otherwise, parse it and convert to local date string
          let taskDateString: string;
          if (typeof task.dueDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(task.dueDate)) {
            // Already in YYYY-MM-DD format
            taskDateString = task.dueDate;
          } else {
            // Parse the date and convert to local date string
            const taskDate = new Date(task.dueDate);
            const taskYear = taskDate.getFullYear();
            const taskMonth = String(taskDate.getMonth() + 1).padStart(2, '0');
            const taskDay = String(taskDate.getDate()).padStart(2, '0');
            taskDateString = `${taskYear}-${taskMonth}-${taskDay}`;
          }
          const matchesToday = taskDateString === todayString;
          console.log('Task filtering:', {
            title: task.title,
            dueDate: task.dueDate,
            taskDateString,
            todayString,
            matchesToday,
            status: task.status,
            willShow: matchesToday
          });
          return matchesToday;
        });
        
        const pendingTasksFiltered = activeTasks.filter(task => {
          if (!task.dueDate) return task.status === 'pending';
          const taskDate = new Date(task.dueDate);
          const isOverdue = taskDate < today && task.status !== 'completed';
          if (isOverdue) {
            task.status = 'overdue';
          }
          return task.status === 'pending' || isOverdue;
        });
        
        setTodayTasks(todayTasksFiltered);
        setPendingTasks(pendingTasksFiltered);
        // Store all active (non-completed) tasks sorted by latest
        setAllTasks(activeTasks);
      }
      
      // Load notifications
      const notificationsRef = collection(db, 'notifications');
      const notificationsQuery = query(
        notificationsRef,
        where('createdBy', '==', user.id),
        orderBy('createdAt', 'desc')
      );
      
      const notificationsSnapshot = await getDocs(notificationsQuery);
      const allNotifications: Notification[] = notificationsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          message: data.message || '',
          type: data.type || 'info',
          caseId: data.caseId || '',
          patientName: data.patientName || '',
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          read: data.read || false,
          createdBy: data.createdBy || user.id
        };
      });
      
      // If no notifications exist, create some sample data for demonstration
      if (allNotifications.length === 0) {
        const today = new Date();
        const sampleNotifications: Notification[] = [
          {
            id: 'sample-notification-1',
            title: language === 'he' ? 'מטופל חדש נרשם' : 'New patient registered',
            message: language === 'he' ? 'מטופל חדש נרשם למערכת' : 'A new patient has been registered in the system',
            type: 'info',
            caseId: 'CASE-001',
            patientName: language === 'he' ? 'יוסי כהן' : 'Yossi Cohen',
            createdAt: today.toISOString(),
            read: false,
            createdBy: user.id
          },
          {
            id: 'sample-notification-2',
            title: language === 'he' ? 'תזכורת פגישה' : 'Meeting reminder',
            message: language === 'he' ? 'יש לך פגישה בעוד שעה' : 'You have a meeting in one hour',
            type: 'warning',
            caseId: 'CASE-002',
            patientName: language === 'he' ? 'שרה לוי' : 'Sara Levi',
            createdAt: today.toISOString(),
            read: false,
            createdBy: user.id
          },
          {
            id: 'sample-notification-3',
            title: language === 'he' ? 'דוח הושלם' : 'Report completed',
            message: language === 'he' ? 'הדוח החודשי הושלם בהצלחה' : 'Monthly report has been completed successfully',
            type: 'success',
            caseId: '',
            createdAt: today.toISOString(),
            read: true,
            createdBy: user.id
          }
        ];
        
        setNotifications(sampleNotifications);
      } else {
        setNotifications(allNotifications);
      }
      
    } catch (error) {
      console.error('Error loading tasks and notifications:', error);
    } finally {
      setIsTasksLoading(false);
      setIsNotificationsLoading(false);
    }
  }, [user, language]);

  const handleTaskStatusChange = useCallback(async (taskId: string, status: 'pending' | 'inProgress' | 'completed') => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, { 
        status: status,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.id || ''
      });
      
      // Refresh tasks
      await loadTasksAndNotifications();
      
      console.log('Task status updated successfully');
    } catch (error) {
      console.error('Error updating task status:', error);
      console.error('Failed to update task status');
    }
  }, [user, loadTasksAndNotifications]);

  const handleMarkNotificationRead = useCallback(async (notificationId: string) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, { 
        read: true,
        readAt: new Date().toISOString()
      });
      
      // Refresh notifications
      await loadTasksAndNotifications();
      
      console.log('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [loadTasksAndNotifications]);

  const handleDeleteTask = useCallback(async (taskId: string) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await deleteDoc(taskRef);
      
      // Refresh tasks
      await loadTasksAndNotifications();
      
      console.log('Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }, [loadTasksAndNotifications]);

  const handleDeleteNotification = useCallback(async (notificationId: string) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await deleteDoc(notificationRef);
      
      // Refresh notifications
      await loadTasksAndNotifications();
      
      console.log('Notification deleted successfully');
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }, [loadTasksAndNotifications]);

  // Load on mount
  useEffect(() => {
    loadTasksAndNotifications();
  }, [loadTasksAndNotifications]);

  return {
    todayTasks,
    pendingTasks,
    allTasks,
    notifications,
    isTasksLoading,
    isNotificationsLoading,
    handleTaskStatusChange,
    handleMarkNotificationRead,
    handleDeleteTask,
    handleDeleteNotification,
    refreshTasks: loadTasksAndNotifications
  };
}


